import type {
  FormControlLabelProps as MuiFormControlLabelProps,
  RadioGroupProps as MuiRadioGroupProps,
  RadioProps as MuiRadioProps,
} from "@mui/material";
import { Radio as MuiRadio, RadioGroup as MuiRadioGroup } from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import type { ReactNode } from "react";
import { useCallback, useImperativeHandle } from "react";

import type { ColorName } from "@citrine/theme";
import type { DataFullstoryId } from "@citrine/types/full-story";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from ".";
import * as sx from "./radio-group.sx";
import { useValidateOnChange } from "./utils";

export interface RadioRef {
  setValue: (value: string, shouldValidate?: boolean) => void;
}

export interface RadioGroupItem<Value extends string = string> extends Omit<
  MuiFormControlLabelProps,
  "color" | "control" | "label"
> {
  value: Value;
  children?: ReactNode;
  color?: ColorName;
  control?: MuiFormControlLabelProps["control"];
  label?: ReactNode;
  "data-fullstoryid"?: DataFullstoryId;
}

export interface RadioGroupProps<
  Shape extends FormikValues,
  Value extends string = string,
> extends Omit<MuiRadioGroupProps, "name"> {
  name: Paths<Shape, Value>;
  options: readonly RadioGroupItem<Value>[];
  disabled?: boolean;
  ref?: React.ForwardedRef<RadioRef>;
  size?: MuiRadioProps["size"];
  validateOnChange?: boolean;
}

export function RadioGroup<
  Shape extends FormikValues,
  Value extends string = string,
>({
  name,
  options,
  disabled,
  onChange,
  ref,
  size = "medium",
  validateOnChange: validateOnChangeProp,
  ...props
}: RadioGroupProps<Shape, Value>) {
  const [field, , helpers] = useField(name);
  const { isSubmitting, validateForm } = useFormikContext();
  const { readonly } = useFormConfiguration();

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  useImperativeHandle(
    ref,
    () => ({
      setValue: helpers.setValue,
    }),
    [helpers.setValue],
  );

  const onChangeCallback = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, c: string) => {
      onChange?.(e, c);
      field.onChange(e);
      if (validateOnChange) {
        await sleep(0);
        validateForm();
      }
    },
    [field, onChange, validateForm, validateOnChange],
  );

  return (
    <MuiRadioGroup {...props} {...field} onChange={onChangeCallback}>
      {options.map(
        ({
          value,
          children,
          color,
          control,
          label,
          disabled: optionDisabled,
          ...optionProps
        }) => (
          <div key={value}>
            <sx.FormControlLabel
              {...optionProps}
              size={size}
              label={label ?? value}
              value={value}
              disabled={disabled || optionDisabled || readonly || isSubmitting}
              control={control ?? <MuiRadio color={color} size={size} />}
            />
            {children}
          </div>
        ),
      )}
    </MuiRadioGroup>
  );
}
